import { createValidator } from 'revalidate';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i;

export const hasMinimumLength = min =>
  createValidator(
    message => (value) => {
      if (value && value.length >= min) {
        return undefined;
      }

      return message;
    },
    field => `${field} must contain at least ${min} characters`
  );

export const hasMaximumLength = max =>
  createValidator(
    message => (value) => {
      if (!value || (value && value.length <= max)) {
        return undefined;
      }

      return message;
    },
    field => `${field} can contain maximum ${max} characters`
  );

export const isValidEmail = createValidator(
  message => (value) => {
    if (value && !EMAIL_REGEX.test(value)) {
      return message;
    }

    return undefined;
  },
  'Invalid email address'
);

export const isInEmailDomains = domains =>
  createValidator(
    message => (value) => {
      if (!value) {
        return message;
      }
      // eslint-disable-next-line no-unused-vars
      const [_, domain] = value.split('@');

      if (!domains.includes(domain)) {
        return message;
      }

      return undefined;
    },
    () =>
      `email domain must be ${
        domains.length > 1 ? `one of ${domains.join(',')}` : `in ${domains[0]}`
      }`
  );

export const isChecked = createValidator(
  message => (value) => {
    if (!value) {
      return message;
    }

    return undefined;
  },
  field => `${field} must be checked`
);

export const isBoolean = createValidator(
  message => (value) => {
    if (typeof value !== 'boolean') {
      return message;
    }

    return undefined;
  },
  field => `${field} must be a boolean`
);

export const isNotEmpty = createValidator(
  message => (value) => {
    if (!value || !value.length) {
      return message;
    }

    return undefined;
  },
  field => `${field} can't be empty`
);

export const isGreatThan = (compareValue, orEqual = false) =>
  createValidator(
    message => (value) => {
      const valueNumber = parseFloat(value);

      if (
        valueNumber > compareValue ||
        (orEqual && valueNumber >= compareValue)
      ) {
        return undefined;
      }

      return message;
    },
    field =>
      `${field} must be greater than${
        orEqual ? ' or equal to' : ''
      } ${compareValue}`
  );

export const isGreatThanField = (
  compareField,
  compareFieldLabel,
  orEqual = false
) =>
  createValidator(
    message => (value, values) => {
      const valueNumber = parseFloat(value);
      const comparedValueNumber = parseFloat(values[compareField]);

      if (
        valueNumber > comparedValueNumber ||
        (orEqual && valueNumber >= comparedValueNumber)
      ) {
        return undefined;
      }

      return message;
    },
    field =>
      `${field} must be greater than${
        orEqual ? ' or equal to' : ''
      } ${compareFieldLabel}`
  );

// Similar to above, but allows blank values
export const isGreatThanFieldIfPresent = (
  compareField,
  compareFieldLabel,
  orEqual = false
) =>
  createValidator(
    message => (value, values) => {
      if (value == null || value === '') {
        return undefined;
      }

      const valueNumber = parseFloat(value);
      const comparedValueNumber = parseFloat(values[compareField]);

      if (
        valueNumber > comparedValueNumber ||
        (orEqual && valueNumber >= comparedValueNumber)
      ) {
        return undefined;
      }

      return message;
    },
    field =>
      `${field} must be greater than${
        orEqual ? ' or equal to' : ''
      } ${compareFieldLabel}`
  );

export const isLessThan = (compareValue, orEqual = false) =>
  createValidator(
    message => (value) => {
      const valueNumber = parseFloat(value);

      if (
        valueNumber < compareValue ||
        (orEqual && valueNumber <= compareValue)
      ) {
        return undefined;
      }

      return message;
    },
    field =>
      `${field} must be less than${
        orEqual ? ' or equal to' : ''
      } ${compareValue}`
  );

export const fileHasExtension = extensions =>
  createValidator(
    message => (file) => {
      if (!extensions.some(ext => file.name.endsWith(ext))) {
        return message;
      }

      return undefined;
    },
    field =>
      `${field} must have one of these extensions: ${extensions.join(', ')}`
  );
